import React, { useState } from "react";
import { ShieldCheck, Check, X, Question, Info } from "@phosphor-icons/react";

/* This page documents the actual role-based permissions enforced in
   /app/backend/server.py via require_admin, require_doctor,
   require_doctor_or_clinic_staff, require_planner, require_production,
   require_sales, require_support, and per-route ownership checks.
   It is intentionally a static read-only matrix, the RBAC source of truth
   lives in the backend. Keep this page in sync when you change a route. */

const ROLES = [
    { id: "admin", label: "Admin", tone: "bg-amber-100 text-amber-800" },
    { id: "doctor", label: "Doctor", tone: "bg-sky-100 text-sky-800" },
    { id: "clinic_staff", label: "Clinic staff", tone: "bg-violet-100 text-violet-800" },
    { id: "counsellor", label: "Patient Engagement Specialist", tone: "bg-emerald-100 text-emerald-800" },
    { id: "planner", label: "Treatment Planner", tone: "bg-indigo-100 text-indigo-800" },
    { id: "production", label: "Production Tech", tone: "bg-orange-100 text-orange-800" },
    { id: "sales", label: "Sales & KAM", tone: "bg-fuchsia-100 text-fuchsia-800" },
    { id: "support", label: "Triage & Support", tone: "bg-rose-100 text-rose-800" },
    { id: "patient", label: "Patient", tone: "bg-slate-100 text-slate-800" },
];

// Y = full grant, N = denied, O = own/scoped only, R = read-only
const GROUPS = [
    {
        title: "Leads",
        rows: [
            { action: "View all leads",           perms: { admin: "Y", counsellor: "O" } },
            { action: "Assign lead to Patient Engagement Specialist", perms: { admin: "Y" } },
            { action: "Change lead status / add note", perms: { counsellor: "O", admin: "Y" } },
            { action: "Handoff lead to doctor",   perms: { counsellor: "O", admin: "Y" } },
        ],
    },
    {
        title: "Cases",
        rows: [
            { action: "Create case (file order)",  perms: { doctor: "Y", clinic_staff: "Y" } },
            { action: "Upload STL / intraoral",    perms: { doctor: "O", clinic_staff: "O" } },
            { action: "Submit case for planning",  perms: { doctor: "O", clinic_staff: "O" } },
            { action: "Start / pick up planning",  perms: { planner: "Y" } },
            { action: "Upload planner setup file", perms: { planner: "Y" } },
            { action: "Mark ready_for_review",     perms: { planner: "Y" } },
            { action: "Approve case",              perms: { doctor: "O" }, note: "Clinic staff explicitly blocked (403)" },
            { action: "Request revision",          perms: { doctor: "O" }, note: "Clinic staff explicitly blocked (403)" },
            { action: "Assign planner to a case",  perms: { admin: "Y" } },
            { action: "Start manufacturing",       perms: { production: "Y" } },
            { action: "Ship aligners",             perms: { production: "Y" } },
            { action: "Comment on a case",         perms: { doctor: "O", clinic_staff: "O", planner: "R", admin: "Y", production: "R", support: "R", patient: "O" } },
        ],
    },
    {
        title: "Patients & Walk-ins",
        rows: [
            { action: "Create walk-in patient",    perms: { doctor: "Y", clinic_staff: "Y", admin: "Y" } },
            { action: "View clinic patient list",  perms: { doctor: "O", clinic_staff: "O", admin: "Y" } },
            { action: "Create treatment plan",     perms: { doctor: "Y", clinic_staff: "Y" } },
        ],
    },
    {
        title: "Doctors & Providers",
        rows: [
            { action: "Approve / reject application", perms: { admin: "Y" } },
            { action: "Edit doctor profile",       perms: { admin: "Y" } },
            { action: "Activate / deactivate doctor", perms: { admin: "Y" } },
            { action: "Manage clinic staff",       perms: { doctor: "O", admin: "Y" }, note: "Doctor manages their own; admin manages anyone's" },
            { action: "Assign clinics to a KAM",   perms: { admin: "Y" } },
            { action: "View 'my clinics'",         perms: { sales: "O" } },
        ],
    },
    {
        title: "COE team (internal staff)",
        rows: [
            { action: "Create COE staff",          perms: { admin: "Y" } },
            { action: "Update / deactivate COE staff", perms: { admin: "Y" } },
            { action: "Access COE portal",         perms: { counsellor: "Y", planner: "Y", production: "Y", sales: "Y", support: "Y" } },
        ],
    },
    {
        title: "Tickets (Support)",
        rows: [
            { action: "Create support ticket",     perms: { doctor: "Y", clinic_staff: "Y", patient: "Y", admin: "Y" } },
            { action: "Change ticket status / add note", perms: { support: "Y" } },
            { action: "View own tickets",          perms: { doctor: "O", clinic_staff: "O", patient: "O" } },
        ],
    },
    {
        title: "Admin oversight",
        rows: [
            { action: "Read audit log",            perms: { admin: "Y" } },
            { action: "Case TAT analytics",        perms: { admin: "Y" } },
            { action: "All appointments / patients / messages", perms: { admin: "Y" } },
        ],
    },
];

const CELL_LEGEND = {
    Y: { icon: Check,    cls: "bg-emerald-500/15 text-emerald-700",  label: "Allowed" },
    N: { icon: X,        cls: "bg-rose-500/10 text-rose-600",        label: "Denied" },
    O: { icon: Check,    cls: "bg-amber-500/15 text-amber-700",      label: "Own / scoped only" },
    R: { icon: Info,     cls: "bg-sky-500/15 text-sky-700",          label: "Read-only" },
};

function Cell({ value, note }) {
    if (!value) {
        return (
            <div className="flex items-center justify-center">
                <X size={14} className="text-rose-400/60" />
            </div>
        );
    }
    const meta = CELL_LEGEND[value] || CELL_LEGEND.N;
    const Icon = meta.icon;
    return (
        <div className={`inline-flex items-center justify-center w-7 h-7 rounded-md ${meta.cls}`} title={note || meta.label}>
            <Icon size={14} weight="bold" />
        </div>
    );
}

export default function AdminPermissions() {
    const [openRow, setOpenRow] = useState(null);

    return (
        <div data-testid="admin-permissions-page">
            <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl font-bold flex items-center gap-2.5">
                        <ShieldCheck size={28} weight="duotone" className="text-primary" />
                        Permissions matrix
                    </h1>
                    <p className="text-muted-foreground mt-1 text-sm max-w-3xl">
                        Read-only reference of which role can perform which action. RBAC is enforced server-side
                        via FastAPI dependencies (<code className="text-[11px] bg-secondary px-1 py-0.5 rounded">require_admin</code>,
                        {" "}<code className="text-[11px] bg-secondary px-1 py-0.5 rounded">require_doctor</code>, etc.) plus
                        per-route ownership checks. Update this grid whenever you change a backend route.
                    </p>
                </div>
            </div>

            {/* Legend */}
            <div className="bg-card border border-border rounded-2xl p-4 mb-5 flex flex-wrap gap-3 text-xs">
                {Object.entries(CELL_LEGEND).map(([k, meta]) => {
                    const Icon = meta.icon;
                    return (
                        <div key={k} className="inline-flex items-center gap-1.5">
                            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md ${meta.cls}`}>
                                <Icon size={12} weight="bold" />
                            </span>
                            <span className="text-foreground/80">{meta.label}</span>
                        </div>
                    );
                })}
                <div className="inline-flex items-center gap-1.5">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-md">
                        <X size={12} className="text-rose-400/60" />
                    </span>
                    <span className="text-foreground/80">No access (blank)</span>
                </div>
            </div>

            {/* Matrix groups */}
            {GROUPS.map((group) => (
                <section key={group.title} className="bg-card border border-border rounded-2xl mb-5 overflow-hidden" data-testid={`permissions-group-${group.title.toLowerCase().replace(/\s+/g, "-")}`}>
                    <header className="px-4 py-3 bg-secondary/40 border-b border-border">
                        <h2 className="text-sm font-display font-bold tracking-wide uppercase text-foreground/80">
                            {group.title}
                        </h2>
                    </header>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[920px]">
                            <thead>
                                <tr className="border-b border-border bg-secondary/20">
                                    <th className="text-left font-semibold px-4 py-2 text-[11px] uppercase tracking-wide text-muted-foreground sticky left-0 bg-secondary/20 z-10">
                                        Action
                                    </th>
                                    {ROLES.map((r) => (
                                        <th key={r.id} className="px-2 py-2 text-center align-bottom">
                                            <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${r.tone}`}>
                                                {r.label}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {group.rows.map((row) => {
                                    const rowKey = `${group.title}-${row.action}`;
                                    return (
                                        <React.Fragment key={rowKey}>
                                            <tr className="border-b border-border last:border-0 hover:bg-secondary/30">
                                                <td className="px-4 py-2.5 font-medium sticky left-0 bg-card hover:bg-secondary/30 z-10">
                                                    <button
                                                        type="button"
                                                        onClick={() => row.note && setOpenRow(openRow === rowKey ? null : rowKey)}
                                                        className="text-left flex items-center gap-1.5"
                                                        data-testid={`perm-row-${rowKey.toLowerCase().replace(/\s+/g, "-")}`}
                                                    >
                                                        {row.action}
                                                        {row.note && <Question size={12} className="text-muted-foreground" />}
                                                    </button>
                                                </td>
                                                {ROLES.map((r) => (
                                                    <td key={r.id} className="px-2 py-2.5 text-center">
                                                        <Cell value={row.perms[r.id]} note={row.note} />
                                                    </td>
                                                ))}
                                            </tr>
                                            {row.note && openRow === rowKey && (
                                                <tr>
                                                    <td colSpan={ROLES.length + 1} className="px-4 py-2 bg-amber-50 border-b border-amber-100 text-xs text-amber-900">
                                                        <Info size={12} className="inline mr-1" /> {row.note}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </section>
            ))}
        </div>
    );
}
